import type { RefObject } from 'react';
import type { ChartDataRaw } from './StatisticsChart';
import { seriesColorForVariant } from './control-options';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, Tooltip, XAxis, YAxis, Legend } from 'recharts';
import {
  AXIS_TICK,
  LEGEND_STYLE,
  TOOLTIP_CONTENT_STYLE,
  TOOLTIP_ITEM_STYLE,
  TOOLTIP_LABEL_STYLE,
} from './StatisticsChart';

type CumulativeChartProps = {
  chartDataRaw: ChartDataRaw | null;
  goal: string;
  goalLabel?: string;
  chartRef?: RefObject<HTMLDivElement | null>;
  /** Colour slots for the experiment, so every chart gives a variant the same colour. */
  colorOrder: readonly number[];
};

export const processCumulativeData = (chartDataRaw: ChartDataRaw | null, goal: string) => {
  if (!chartDataRaw) return { chartData: [], categories: [], variantIds: [] };

  const variants = Object.entries(chartDataRaw);
  if (variants.length === 0) return { chartData: [], categories: [], variantIds: [] };

  const allDates = new Set<string>();
  for (const [, variant] of variants) {
    for (const point of variant.data) {
      allDates.add(point.date);
    }
  }

  const sortedDates = Array.from(allDates).sort();

  // Build cumulative totals per variant
  const cumulativeTotals: Record<string, number> = {};
  const cumulativeCounts: Record<string, number> = {};

  for (const [, variant] of variants) {
    cumulativeTotals[variant.variantName] = 0;
    cumulativeCounts[variant.variantName] = 0;
  }

  const chartData = sortedDates.map((date) => {
    const point: Record<string, string | number> = { date };
    for (const [, variant] of variants) {
      const dataPoint = variant.data.find((d) => d.date === date);
      const dailyValue = dataPoint?.value || 0;

      cumulativeTotals[variant.variantName]! += goal === 'Revenue' ? dailyValue / 100 : dailyValue;
      cumulativeCounts[variant.variantName]! += 1;

      // Cumulative average (running rate)
      const count = cumulativeCounts[variant.variantName]!;
      point[variant.variantName] =
        count > 0 ? Math.round((cumulativeTotals[variant.variantName]! / count) * 100) / 100 : 0;
    }
    return point;
  });

  const categories = variants.map(([, v]) => v.variantName);
  // Parallel to `categories`: the colour is chosen by variant, not by position here.
  const variantIds = variants.map(([id]) => id);

  return { chartData, categories, variantIds };
};

export const CumulativeChart = ({ chartDataRaw, goal, goalLabel, chartRef, colorOrder }: CumulativeChartProps) => {
  const { chartData, categories, variantIds } = processCumulativeData(chartDataRaw, goal);

  if (chartData.length === 0) return null;

  return (
    <div ref={chartRef} className="border-line bg-panel2 rounded-card border">
      <div className="border-line border-b px-4 py-3">
        <h3 className="text-foreground text-title font-medium tracking-[-0.01em]">Cumulative {goalLabel ?? goal}</h3>
        <p className="supporting">
          Running average {(goalLabel ?? goal).toLowerCase()} per variant, showing trend stability over time.
        </p>
      </div>

      <div className="p-4">
        <ResponsiveContainer width="100%" height={384}>
          <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <defs>
              {categories.map((category, idx) => (
                <linearGradient key={category} id={`cumulative-color-${idx}`} x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={seriesColorForVariant(variantIds[idx]!, colorOrder)}
                    stopOpacity={0.22}
                  />
                  <stop offset="95%" stopColor={seriesColorForVariant(variantIds[idx]!, colorOrder)} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              tick={AXIS_TICK}
              tickMargin={8}
              minTickGap={24}
              stroke="var(--line)"
              tickLine={false}
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getDate()}/${date.getMonth() + 1}`;
              }}
            />
            <YAxis
              width={56}
              tick={AXIS_TICK}
              stroke="var(--line)"
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => value.toFixed(1)}
            />
            <Tooltip
              cursor={{ stroke: 'var(--line)', strokeWidth: 1 }}
              contentStyle={TOOLTIP_CONTENT_STYLE}
              labelStyle={TOOLTIP_LABEL_STYLE}
              itemStyle={TOOLTIP_ITEM_STYLE}
              formatter={(value: number | undefined, name: string | undefined) => {
                if (value === undefined) return [0, name ?? ''];
                return [value.toFixed(2), name ?? ''];
              }}
              labelFormatter={(label) => new Date(label).toLocaleDateString()}
            />
            <Legend wrapperStyle={LEGEND_STYLE} iconType="plainline" iconSize={12} />
            {categories.map((category, idx) => (
              <Area
                key={category}
                type="monotone"
                dataKey={category}
                stroke={seriesColorForVariant(variantIds[idx]!, colorOrder)}
                activeDot={{
                  r: 4,
                  stroke: seriesColorForVariant(variantIds[idx]!, colorOrder),
                  strokeWidth: 2,
                  fill: 'hsl(var(--panel))',
                }}
                fillOpacity={1}
                fill={`url(#cumulative-color-${idx})`}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
